import React, { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Paperclip, Archive, Trash2, Mail, MailOpen, Check, Ban, Clock, ExternalLink, FolderInput } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getDateTimeDisplay } from '@/lib/dateDisplay';
import { getBrandfetchLogoUrl } from '@/lib/avatar';
import { createEmailDragPreview } from '@/lib/dragPreview';
import type { SenderGroupQuickAction, SenderGroupInfo } from '../types';
import type { SenderGroupEntry } from '../hooks/useSenderGrouping';
import { useSenderSubjects } from '../hooks/useSenderSubjects';
import { getInitials } from '../utils/emailFormatting';
import { useHoverStore } from '@/stores/hoverStore';
import { useSenderGroupingStore } from '@/stores/senderGroupingStore';
import { useMoveToFolderStore } from '@/stores/moveToFolderStore';
import { useBulkOperationsStore } from '@/stores/bulkOperationsStore';
import { useLongPress } from '@/hooks/useLongPress';
import { useIsMobile, useIsTouch } from '@/hooks/useIsMobile';
import { SwipeableRow, type SwipeActionConfig, type SwipeableRowHandle } from './SwipeableRow';
import { RowHoverActions, HoverActionButton } from './RowHoverActions';
import { motion } from 'motion/react';
import { easings } from '@/lib/animations';
import type { Email } from '@/types/email';

const MAX_STAGGERED_ANIMATION_ITEMS = 0;
const STAGGER_STEP_SECONDS = 0.03;

interface SenderGroupItemProps {
  group: SenderGroupEntry;
  index: number;
  showAccountColors: boolean;
  showRecipientNames?: boolean;
  contactNameLookup?: ReadonlyMap<string, string>;
  accountColors?: Record<string, string | null | undefined>;
  hasMore: boolean;
  onSenderGroupSelect?: (group: SenderGroupInfo) => void;
  onSenderGroupOpenLatestEmail?: (group: SenderGroupInfo, email: Email) => void;
  onSenderGroupDragStart?: (group: SenderGroupInfo) => void;
  onSenderGroupDragEnd?: () => void;
  onSenderGroupAction?: (group: SenderGroupInfo, action: SenderGroupQuickAction) => Promise<void> | void;
  senderGroupAction?: 'archive' | 'delete' | 'block' | 'approve' | null;
  draggingState: {
    getGroupDragState: (groupKey: string, fallbackCount: number) => { isDragging: boolean; count: number };
  };
  bulkSelection?: Set<string>;
  onToggleBulkSelect?: (emailId: string) => void;
  isBulkEditMode?: boolean;
  activeLabelId?: string | null;
}

export const SenderGroupItem = React.memo(function SenderGroupItem({
  group,
  index,
  showAccountColors,
  showRecipientNames = false,
  contactNameLookup,
  accountColors,
  hasMore,
  onSenderGroupSelect,
  onSenderGroupOpenLatestEmail,
  onSenderGroupDragStart,
  onSenderGroupDragEnd,
  onSenderGroupAction,
  senderGroupAction = null,
  draggingState,
  bulkSelection,
  onToggleBulkSelect,
  isBulkEditMode = false,
  activeLabelId = null,
}: SenderGroupItemProps) {
  const [groupActionLoading, setGroupActionLoading] = useState<{ key: string; action: SenderGroupQuickAction } | null>(null);
  const setHoveredSenderGroup = useHoverStore((state) => state.setHoveredSenderGroup);
  const clearHover = useHoverStore((state) => state.clearHover);
  const prefetchSenderGroup = useSenderGroupingStore((state) => state.prefetchSenderGroup);
  const swipeRowRef = useRef<SwipeableRowHandle>(null);
  const swipeDisabled = isBulkEditMode;

  const firstEmail = group.emails[0];

  const messageCount = group.emails.length;
  const knownTotal =
    typeof group.totalMessages === 'number' && Number.isFinite(group.totalMessages)
      ? Math.max(group.totalMessages, messageCount)
      : messageCount;

  // Build unique account/folder contexts from the group's emails
  // This ensures we fetch subjects from all accounts in unified inbox
  const contexts = useMemo(() => {
    const contextMap = new Map<string, { accountId: string; folderId: string }>();
    group.emails.forEach((email) => {
      if (email.accountId && email.folderId) {
        const key = `${email.accountId}:${email.folderId}`;
        if (!contextMap.has(key)) {
          contextMap.set(key, { accountId: email.accountId, folderId: email.folderId });
        }
      }
    });
    return Array.from(contextMap.values());
  }, [group.emails]);

  // Fetch subjects from all accounts this sender appears in
  const shouldFetchSubjects = !activeLabelId;
  const { subjects: fetchedSubjects } = useSenderSubjects({
    contexts,
    sender: {
      contactId: group.contactId,
      email: group.senderEmail || firstEmail?.from.email,
    },
    enabled: !!firstEmail && shouldFetchSubjects,
  });

  // Merge subjects from both sources to ensure complete list:
  // - group.emails: always up-to-date (includes new emails from realtime events, emails from all accounts)
  // - fetchedSubjects: fills in subjects for emails not yet loaded in the view
  const subjects = useMemo(() => {
    // For label filter, use only group.emails (fetchedSubjects is disabled)
    if (activeLabelId) {
      return group.emails
        .map((message) => ({
          subject: (message.subject || 'No subject').trim(),
          date: new Date(message.date),
        }))
        .filter((s) => s.subject.length > 0)
        .sort((a, b) => b.date.getTime() - a.date.getTime())
        .map((s) => s.subject);
    }

    // Merge subjects from both sources, deduplicating by email ID (not subject text)
    // This allows duplicate subjects from different emails while avoiding
    // showing the same email's subject twice
    const seenIds = new Set<string>();
    const allSubjects: Array<{ subject: string; date: Date }> = [];

    // Add subjects from group.emails first (includes new emails from realtime events)
    group.emails.forEach((message) => {
      const subject = (message.subject || 'No subject').trim();
      if (subject.length > 0 && !seenIds.has(message.id)) {
        seenIds.add(message.id);
        allSubjects.push({ subject, date: new Date(message.date) });
      }
    });

    // Add subjects from fetchedSubjects (fills in emails not yet loaded in view)
    // Skip any that we already have from group.emails
    fetchedSubjects.forEach((s) => {
      const subject = (s.subject || 'No subject').trim();
      if (subject.length > 0 && !seenIds.has(s.id)) {
        seenIds.add(s.id);
        const date = s.date instanceof Date ? s.date : new Date(s.date);
        allSubjects.push({ subject, date });
      }
    });

    // Sort by date descending and extract just the subject strings
    return allSubjects
      .sort((a, b) => b.date.getTime() - a.date.getTime())
      .map((s) => s.subject);
  }, [activeLabelId, fetchedSubjects, group.emails]);

  const MAX_VISIBLE_SUBJECTS = 12;
  const displayedSubjects = subjects.slice(0, MAX_VISIBLE_SUBJECTS);
  // Show '…' if we have more subjects OR if the group has more total messages than we fetched
  const hasMoreSubjects = subjects.length > displayedSubjects.length ||
    (knownTotal > subjects.length && subjects.length >= MAX_VISIBLE_SUBJECTS);
  const subjectList = hasMoreSubjects
    ? [...displayedSubjects, '…']
    : displayedSubjects;
  const subjectLine = subjectList.length > 0 ? subjectList.join(' • ') : 'No subject';

  const resolvePrimaryRecipient = () => {
    if (!firstEmail) {
      return null;
    }
    const lists = [firstEmail.to, firstEmail.cc, firstEmail.bcc, firstEmail.replyTo];
    for (const list of lists) {
      if (!Array.isArray(list)) {
        continue;
      }
      const match = list.find((entry) => entry?.email);
      if (match) {
        return match;
      }
    }
    return null;
  };
  const primaryRecipient = resolvePrimaryRecipient();
  const recipientEmailKey =
    showRecipientNames && primaryRecipient?.email ? primaryRecipient.email.trim().toLowerCase() : null;
  const contactRecipientName =
    recipientEmailKey && contactNameLookup ? contactNameLookup.get(recipientEmailKey) ?? null : null;
  const recipientDisplayName =
    (showRecipientNames && contactRecipientName) ||
    group.senderName ||
    primaryRecipient?.name ||
    primaryRecipient?.email ||
    group.displayEmail;
  const senderDisplayName =
    group.senderName || firstEmail.from.name || firstEmail.from.email || group.displayEmail;
  const displayLabel = showRecipientNames ? `To: ${recipientDisplayName}` : senderDisplayName;
  const avatarEmail = showRecipientNames
    ? primaryRecipient?.email || group.senderEmail || firstEmail.from.email
    : firstEmail.from.email;
  const avatarSrc = avatarEmail ? getBrandfetchLogoUrl(avatarEmail) : null;
  const avatarFallbackEmail = avatarEmail || group.displayEmail;
  const accountColor = accountColors?.[firstEmail.accountId] || null;
  const showRing = showAccountColors && Boolean(accountColor);
  const ringColor = accountColor || '#2563eb';
  const hasUnread = group.unreadCount > 0;
  // Use subtle indicators instead of full row background for professional look
  const itemStyle: CSSProperties = {};

  const hasAttachments = useMemo(
    () => group.emails.some((message) =>
      message.attachments?.some(
        (attachment) => attachment.validated !== false && !attachment.inline && !attachment.contentId,
      ),
    ),
    [group.emails],
  );

  const { shortLabel: latestDate, fullLabel: latestFullDate } = useMemo(
    () => getDateTimeDisplay(firstEmail.date),
    [firstEmail.date],
  );
  const contactStatus =
    group.contactApprovalState && group.contactApprovalState !== 'approved'
      ? group.contactApprovalState
      : null;
  const contactStatusIcon = contactStatus ? (
    <Tooltip>
      <TooltipTrigger asChild>
        {contactStatus === 'blocked' ? (
          <Ban className="h-3.5 w-3.5 flex-shrink-0 text-destructive" />
        ) : (
          <Clock className="h-3.5 w-3.5 flex-shrink-0 text-warning-ink" />
        )}
      </TooltipTrigger>
      <TooltipContent>
        {contactStatus === 'blocked' ? 'Blocked sender' : 'Pending approval'}
      </TooltipContent>
    </Tooltip>
  ) : null;

  const hasServerTotal = typeof group.totalMessages === 'number' && Number.isFinite(group.totalMessages);
  const showPlaceholder = !hasServerTotal && hasMore;
  const effectiveMessageCount = knownTotal;
  const badgeLabel = showPlaceholder ? '…' : String(effectiveMessageCount);
  const badgeVariant: 'secondary' | 'outline' = showPlaceholder ? 'outline' : 'secondary';

  // Only animate entry for the first batch of items to prevent jarring animations when list updates
  const shouldAnimateEntry = index < MAX_STAGGERED_ANIMATION_ITEMS;
  const animationDelay = shouldAnimateEntry ? index * STAGGER_STEP_SECONDS : 0;

  const senderGroupInfo: SenderGroupInfo = useMemo(
    () => ({
      key: group.key,
      contactId: group.contactId ?? null,
      contactApprovalState: group.contactApprovalState ?? null,
      senderEmail: group.senderEmail || firstEmail.from.email || group.displayEmail,
      senderName: senderDisplayName,
      emails: group.emails,
      unreadCount: group.unreadCount,
      totalMessages: effectiveMessageCount,
    }),
    [
      effectiveMessageCount,
      firstEmail.from.email,
      group.contactApprovalState,
      group.contactId,
      group.displayEmail,
      group.emails,
      group.key,
      group.senderEmail,
      group.unreadCount,
      senderDisplayName,
    ],
  );

  const dragState = draggingState.getGroupDragState(group.key, effectiveMessageCount);
  const isGroupBusy = groupActionLoading?.key === group.key;
  const markQuickAction: SenderGroupQuickAction =
    group.unreadCount > 0 ? 'markRead' : 'markUnread';
  const markButtonIcon = group.unreadCount > 0 ? <MailOpen className="h-4 w-4" /> : <Mail className="h-4 w-4" />;
  const archiveDisabled =
    senderGroupAction === 'archive' || isGroupBusy || !onSenderGroupAction || !group.emails.length;
  const deleteDisabled =
    senderGroupAction === 'delete' || isGroupBusy || !onSenderGroupAction || !group.emails.length;
  const markDisabled =
    isGroupBusy ||
    !onSenderGroupAction ||
    (markQuickAction === 'markRead' && group.unreadCount === 0) ||
    (markQuickAction === 'markUnread' && group.unreadCount === group.emails.length);
  const contactPending = senderGroupInfo.contactApprovalState === 'pending';
  const contactBlocked = senderGroupInfo.contactApprovalState === 'blocked';
  const contactIdAvailable =
    Boolean(senderGroupInfo.contactId) ||
    senderGroupInfo.emails.some((email) => Boolean(email.contactId));
  const showApproveAction = contactPending || contactBlocked;
  const showBlockAction = contactPending;
  const showContactActions = showApproveAction || showBlockAction;
  const approveDisabled =
    isGroupBusy || !onSenderGroupAction || !contactIdAvailable || !showApproveAction;
  const blockDisabled =
    isGroupBusy || !onSenderGroupAction || !contactPending || !contactIdAvailable;
  const approveButtonIcon = <Check className="h-4 w-4" />;
  const blockButtonIcon = <Ban className="h-4 w-4" />;
  const showHoverActions = Boolean(onSenderGroupAction || onSenderGroupOpenLatestEmail);

  const handleQuickAction = useCallback(
    async (action: SenderGroupQuickAction) => {
      if (!onSenderGroupAction) {
        return;
      }
      setGroupActionLoading({ key: group.key, action });
      try {
        await onSenderGroupAction(senderGroupInfo, action);
      } catch (error) {
        console.error('Failed to execute sender group action', error);
      } finally {
        setGroupActionLoading((prev) => (prev && prev.key === group.key ? null : prev));
      }
    },
    [group.key, onSenderGroupAction, senderGroupInfo],
  );

  const handleDragStart = useCallback(
    (event: DragEvent) => {
      if (!senderGroupInfo.emails.length || isBulkEditMode) {
        return;
      }
      const dataTransfer = event.dataTransfer;
      if (!dataTransfer) {
        return;
      }
      dataTransfer.effectAllowed = 'move';
      const messageText = `${displayLabel} • ${dragState.count} ${
        dragState.count === 1 ? 'message' : 'messages'
      }`;
      dataTransfer.setData('text/plain', messageText);
      dataTransfer.setData('application/email-count', String(dragState.count));
      dataTransfer.setData(
        'application/email-id-list',
        JSON.stringify(senderGroupInfo.emails.map((email) => email.id)),
      );
      createEmailDragPreview(messageText, event);
      onSenderGroupDragStart?.(senderGroupInfo);
    },
    [dragState.count, displayLabel, isBulkEditMode, onSenderGroupDragStart, senderGroupInfo],
  );

  const handleDragEnd = useCallback(() => {
    onSenderGroupDragEnd?.();
  }, [onSenderGroupDragEnd]);

  const motionRef = useRef<HTMLDivElement | null>(null);
  const setMotionRef = useCallback((node: HTMLDivElement | null) => {
    motionRef.current = node;
  }, []);

  useEffect(() => {
    const element = motionRef.current;
    if (!element || isBulkEditMode) {
      return;
    }
    element.addEventListener('dragstart', handleDragStart as EventListener);
    element.addEventListener('dragend', handleDragEnd as EventListener);
    return () => {
      element.removeEventListener('dragstart', handleDragStart as EventListener);
      element.removeEventListener('dragend', handleDragEnd as EventListener);
    };
  }, [handleDragStart, handleDragEnd, isBulkEditMode]);

  // Swipe layout mirrors EmailListItem (iOS Mail convention): archive alone on
  // the left rail (full swipe right = archive all); read/unread, move, delete
  // on the right with delete at the screen edge. Approve/block for pending
  // senders stay reachable via the digest header and the bulk action bar.
  const leftSwipeActions = useMemo<SwipeActionConfig[]>(
    () => [
      {
        key: 'archive',
        label: 'Archive',
        icon: <Archive className="h-5 w-5" />,
        tone: 'accent',
        disabled: archiveDisabled,
        commitStyle: 'fly',
        onAction: () => {
          void handleQuickAction('archive');
        },
      },
    ],
    [archiveDisabled, handleQuickAction],
  );

  const rightSwipeActions = useMemo<SwipeActionConfig[]>(
    () => [
      {
        key: markQuickAction,
        label: markQuickAction === 'markRead' ? 'Read' : 'Unread',
        icon:
          markQuickAction === 'markRead' ? (
            <MailOpen className="h-5 w-5" />
          ) : (
            <Mail className="h-5 w-5" />
          ),
        tone: 'primary',
        disabled: markDisabled,
        onAction: () => {
          void handleQuickAction(markQuickAction);
        },
      },
      {
        key: 'move',
        label: 'Move',
        icon: <FolderInput className="h-5 w-5" />,
        tone: 'muted',
        onAction: () => {
          useMoveToFolderStore.getState().open(group.emails.map((email) => email.id));
        },
      },
      {
        key: 'delete',
        label: 'Delete',
        icon: <Trash2 className="h-5 w-5" />,
        tone: 'destructive',
        disabled: deleteDisabled,
        commitStyle: 'fly',
        onAction: () => {
          void handleQuickAction('delete');
        },
      },
    ],
    [deleteDisabled, group.emails, handleQuickAction, markDisabled, markQuickAction],
  );

  const allEmailsInGroupSelected = useMemo(
    () => bulkSelection && group.emails.length > 0
      ? group.emails.every((email) => bulkSelection.has(email.id))
      : false,
    [bulkSelection, group.emails],
  );

  const handleMouseEnter = useCallback(() => {
    const emailIds = group.emails.map((email) => email.id);
    setHoveredSenderGroup(group.key, emailIds, senderGroupInfo);
    prefetchSenderGroup(group);
  }, [group, senderGroupInfo, setHoveredSenderGroup, prefetchSenderGroup]);

  const handleGroupBulkToggle = useCallback(() => {
    if (!onToggleBulkSelect || !bulkSelection) {
      return;
    }
    const allSelected = group.emails.every((email) => bulkSelection.has(email.id));
    group.emails.forEach((email) => {
      const shouldSelect = !allSelected;
      const isCurrentlySelected = bulkSelection.has(email.id);
      if (shouldSelect !== isCurrentlySelected) {
        onToggleBulkSelect(email.id);
      }
    });
  }, [bulkSelection, group.emails, onToggleBulkSelect]);

  // Round 4: selection replaces the bulk-mode toggle button. Long-press
  // (touch) enters bulk mode with this group selected; on desktop a checkbox
  // surfaces over the avatar on hover.
  const setBulkEditMode = useBulkOperationsStore((state) => state.setBulkEditMode);
  // Touch at any width: the Duo's inner screen is md+ but still a finger.
  const isNarrow = useIsMobile();
  const isTouch = useIsTouch();
  const isMobile = isNarrow || isTouch;
  const enterBulkSelect = useCallback(() => {
    setBulkEditMode(true);
    if (!allEmailsInGroupSelected) {
      // Select every email in the group via the store directly — the
      // bulkSelection prop is stale inside this callback right after the mode
      // flips.
      const { bulkSelection: liveSelection, toggleSelection } = useBulkOperationsStore.getState();
      group.emails.forEach((email) => {
        if (!liveSelection.has(email.id)) {
          toggleSelection(email.id);
        }
      });
    }
  }, [setBulkEditMode, allEmailsInGroupSelected, group.emails]);
  const longPressHandlers = useLongPress(enterBulkSelect, { disabled: isBulkEditMode });

  return (
    <SwipeableRow
      ref={swipeRowRef}
      className="group"
      contentClassName="rounded-xl"
      leftActions={leftSwipeActions}
      rightActions={rightSwipeActions}
      disabled={swipeDisabled}
    >
      <motion.div
        initial={
          shouldAnimateEntry
            ? { opacity: 0, y: 20, scale: 0.96 }
            : { opacity: 1, y: 0, scale: 1 }
        }
        ref={setMotionRef}
        animate={
          dragState.isDragging
            ? {
                opacity: 0.5,
                scale: 0.98,
                y: -2,
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
              }
            : { opacity: 1, y: 0, scale: 1 }
        }
        exit={{ opacity: 0, x: -30, scale: 0.95 }}
        transition={{
          ...easings.springGentle,
          delay: animationDelay,
        }}
        whileHover={!isBulkEditMode ? { y: -2, scale: 1.005 } : undefined}
        whileTap={!isBulkEditMode ? { scale: 0.98 } : undefined}
        className={cn(
          'relative flex w-full gap-3 rounded-xl transition-all duration-200 box-border overflow-hidden glass-item',
          'items-stretch p-3 md:items-center md:py-2 md:px-4',
          !isBulkEditMode && 'cursor-grab active:cursor-grabbing',
          isBulkEditMode && 'cursor-pointer',
          dragState.isDragging && 'ring-2 ring-primary/60 opacity-50',
        )}
        style={itemStyle}
        draggable={!isBulkEditMode}
        // Mouse only: hover state + prefetch mean nothing for a finger, and on
        // iOS a mouseenter handler that changes the page can swallow the tap.
        onPointerEnter={(event) => {
          if (event.pointerType === 'mouse') {
            handleMouseEnter();
          }
        }}
        onPointerLeave={() => {
          clearHover();
        }}
        {...longPressHandlers}
        // Anchor for keyboard shortcuts: hovering this group row and pressing a
        // shortcut applies the equivalent bulk action to ALL emails in the group,
        // resolved from the live DOM at keypress so it's robust to scroll (§2.17 #5).
        data-sender-group-email-ids={group.emails.map((email) => email.id).join(',')}
        data-sender-group-key={group.key}
        aria-grabbed={dragState.isDragging}
        title={
          isBulkEditMode
            ? undefined
            : `Drag to move ${dragState.count} ${dragState.count === 1 ? 'message' : 'messages'} from ${displayLabel}`
        }
      >

        {isBulkEditMode && bulkSelection && onToggleBulkSelect && (
          <div className="flex items-center gap-3 pl-2">
            <Checkbox
              checked={allEmailsInGroupSelected}
              onCheckedChange={handleGroupBulkToggle}
            />
          </div>
        )}

        <div
          className="flex flex-1 gap-3 overflow-hidden items-center"
          role="button"
          tabIndex={0}
          onClick={(event) => {
            if (swipeRowRef.current?.openSide()) {
              event.preventDefault();
              event.stopPropagation();
              swipeRowRef.current?.close();
              return;
            }
            // In bulk mode on touch screens the whole row toggles the group.
            if (isBulkEditMode && isMobile) {
              handleGroupBulkToggle();
              return;
            }
            onSenderGroupSelect?.(senderGroupInfo);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              if (swipeRowRef.current?.openSide()) {
                event.preventDefault();
                swipeRowRef.current?.close();
                return;
              }
              event.preventDefault();
              onSenderGroupSelect?.(senderGroupInfo);
            }
          }}
        >
          <div className="relative flex-shrink-0">
            <Avatar
              className="h-10 w-10 m-1 flex-shrink-0"
              style={showRing
                ? {
                    boxShadow: `0 0 0 2px ${ringColor}`,
                  }
                : undefined}
            >
              {avatarSrc && (
                <AvatarImage src={avatarSrc} alt={displayLabel || group.displayEmail} />
              )}
              <AvatarFallback className="text-xs">
                {getInitials(displayLabel, avatarFallbackEmail)}
              </AvatarFallback>
            </Avatar>
            {contactStatusIcon && (
              <div className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-background border border-border shadow-sm">
                {contactStatusIcon}
              </div>
            )}
            {/* Desktop selection entry: checkbox surfaces over the avatar on
                hover; clicking it selects the whole group (no standing toggle). */}
            {!isBulkEditMode && onToggleBulkSelect && bulkSelection && (
              <div
                className="absolute inset-0 z-10 hidden items-center justify-center rounded-full bg-card/95 md:group-hover:flex"
                onClick={(event) => {
                  event.stopPropagation();
                  enterBulkSelect();
                }}
              >
                <Checkbox checked={allEmailsInGroupSelected} className="pointer-events-none" />
              </div>
            )}
          </div>
          {/* Desktop layout - horizontal row */}
          <div className="hidden md:flex min-w-0 flex-1 overflow-hidden cursor-pointer items-center gap-3">
            <div className="flex items-center gap-2 flex-shrink-0 w-[150px] lg:w-[220px] lg:min-w-[200px] lg:max-w-[260px]">
              <span
                className={cn(
                  'min-w-0 truncate text-sm whitespace-nowrap',
                  hasUnread && 'font-bold',
                )}
              >
                {displayLabel}
              </span>
              <Badge variant={badgeVariant} className="text-xs px-2 py-0 flex items-center flex-shrink-0">
                {badgeLabel}
              </Badge>
            </div>
            <div
              className={cn(
                'min-w-0 flex-1 text-sm text-foreground line-clamp-2',
                hasUnread && 'font-bold',
              )}
            >
              {subjectLine}
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
              {hasAttachments && <Paperclip className="h-3 w-3 text-muted-foreground" />}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="w-[68px] text-right">{latestDate}</span>
                </TooltipTrigger>
                <TooltipContent side="top" align="end">
                  {latestFullDate}
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          {/* Mobile layout - vertical stack */}
          <div className="flex flex-col md:hidden min-w-0 flex-1 overflow-hidden cursor-pointer">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className={cn('truncate text-sm', hasUnread && 'font-bold')}>
                  {displayLabel}
                </span>
                <Badge variant={badgeVariant} className="text-xs px-2 py-0 flex items-center flex-shrink-0">
                  {badgeLabel}
                </Badge>
              </div>
              <div className="flex items-center gap-0.5 flex-shrink-0">
                {hasAttachments && <Paperclip className="h-3 w-3 text-muted-foreground" />}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-xs text-muted-foreground w-[55px] text-right">
                      {latestDate}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" align="end">
                    {latestFullDate}
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
            <p
              className={cn(
                'mt-1.5 line-clamp-2 text-sm text-foreground',
                hasUnread && 'font-bold',
              )}
            >
              {subjectLine}
            </p>
          </div>
        </div>
      </motion.div>
      {dragState.isDragging && (
        <div className="pointer-events-none absolute bottom-3 right-3 flex items-center gap-1 rounded-full bg-background/80 px-2 py-1 text-xs text-muted-foreground shadow-sm ring-1 ring-border/60">
          Moving {dragState.count} {dragState.count === 1 ? 'message' : 'messages'}
        </div>
      )}
      {/* Fixed slot layout (RowHoverActions): the core cluster and date sit at
          the same x-positions as email rows, so grouped lists that mix both
          row types keep archive/delete/mark aligned. Open-latest occupies the
          first middle slot (where Reply sits on email rows); the other two
          slots are reserved spacers. Approve/block go in `leading`. */}
      {showHoverActions && (
        <RowHoverActions
          leading={
            onSenderGroupAction && showContactActions ? (
              <>
                <HoverActionButton
                  action={{
                    key: 'approve',
                    icon: approveButtonIcon,
                    label: 'Approve sender',
                    tone: 'success',
                    disabled: approveDisabled,
                    onAction: () => void handleQuickAction('approveSender'),
                  }}
                />
                {showBlockAction && (
                  <HoverActionButton
                    action={{
                      key: 'block',
                      icon: blockButtonIcon,
                      label: 'Block sender',
                      tone: 'destructive',
                      disabled: blockDisabled,
                      onAction: () => void handleQuickAction('blockSender'),
                    }}
                  />
                )}
              </>
            ) : undefined
          }
          primary={
            onSenderGroupAction
              ? [
                  {
                    key: 'archive',
                    icon: <Archive className="h-4 w-4" />,
                    label: 'Archive all',
                    disabled: archiveDisabled,
                    onAction: () => void handleQuickAction('archive'),
                  },
                  {
                    key: 'delete',
                    icon: <Trash2 className="h-4 w-4" />,
                    label: 'Delete all',
                    tone: 'destructive',
                    disabled: deleteDisabled,
                    onAction: () => void handleQuickAction('delete'),
                  },
                  {
                    key: 'mark',
                    icon: markButtonIcon,
                    label: markQuickAction === 'markRead' ? 'Mark all read' : 'Mark all unread',
                    disabled: markDisabled,
                    onAction: () => void handleQuickAction(markQuickAction),
                  },
                ]
              : []
          }
          middle={[
            onSenderGroupOpenLatestEmail && firstEmail
              ? {
                  key: 'open-latest',
                  icon: <ExternalLink className="h-4 w-4" />,
                  label: 'Open latest email',
                  onAction: () => onSenderGroupOpenLatestEmail(senderGroupInfo, firstEmail),
                }
              : null,
            null,
            null,
          ]}
          overflow={
            <DropdownMenuItem
              onClick={(event) => {
                event.stopPropagation();
                useMoveToFolderStore.getState().open(group.emails.map((email) => email.id));
              }}
              disabled={!group.emails.length}
              className="gap-2"
            >
              <FolderInput className="h-4 w-4" />
              <span>Move all to folder</span>
            </DropdownMenuItem>
          }
          dateShort={latestDate}
          dateFull={latestFullDate}
        />
      )}
    </SwipeableRow>
  );
}, areSenderGroupPropsEqual);

function areSenderGroupPropsEqual(
  prev: SenderGroupItemProps,
  next: SenderGroupItemProps,
): boolean {
  if (prev.group !== next.group) return false;
  if (prev.index !== next.index) return false;
  if (prev.showAccountColors !== next.showAccountColors) return false;
  if (prev.showRecipientNames !== next.showRecipientNames) return false;
  if (prev.hasMore !== next.hasMore) return false;
  if (prev.senderGroupAction !== next.senderGroupAction) return false;
  if (prev.bulkSelection !== next.bulkSelection) return false;
  if (prev.isBulkEditMode !== next.isBulkEditMode) return false;
  if (prev.activeLabelId !== next.activeLabelId) return false;
  if (prev.contactNameLookup !== next.contactNameLookup) return false;
  if (prev.accountColors !== next.accountColors) return false;
  return true;
}
